// src/pages/SubAccountConsent.js
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import TextInput from "../components/TextInput";
import FileUploader from "../components/FileUploader";
import SignaturePad from "../components/SignaturePad";
import TermsModal from "../components/TermsModal";
// 1) Import the API function
import { submitSubAccountConsent } from "../services/api";

// Helper function: formats a name so that only the first letter is uppercase and the rest lowercase.
const formatName = (name) => {
  if (!name) return "";
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

function SubAccountConsent() {
  const location = useLocation();

  // -----------------------------------------------------------------------
  // Define the initial form data, matching what the backend expects.
  // -----------------------------------------------------------------------
  const [formData, setFormData] = useState({
    registration_id: null,
    partner_first_name: "",
    partner_last_name: "",
    main_first_name: "",
    main_last_name: "",
    email: "",
    main_user_code: "",
    phone_number: "",
    address_line_1: "",
    address_line_2: "",
    address_city: "",
    postcode: "",
    county: "",
    // Consent tick boxes (matching backend field names)
    consent_for_purchases: false,
    confirm_correct_information: false,
    data_sharing_privacy_consent: false,
    rules_consent: false,
    file: null,
    signature_base64: "",
    agree_terms: false,
  });

  // Control the Terms & Conditions modal display
  const [isModalOpen, setIsModalOpen] = useState(false);

  // -----------------------------------------------------------------------
  // Parse query parameters from the URL and pre-fill the form data
  // -----------------------------------------------------------------------
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const regId = params.get("registration_id");
    setFormData((prev) => ({
      ...prev,
      registration_id: regId ? parseInt(regId, 10) : null,
      partner_first_name: params.get("partner_first_name") || "",
      partner_last_name: params.get("partner_last_name") || "",
      main_first_name: params.get("main_first_name") || "",
      main_last_name: params.get("main_last_name") || "",
      email: params.get("email") || "",
      main_user_code: params.get("main_user_code") || "",
    }));
  }, [location]);

  // -----------------------------------------------------------------------
  // Handle changes for text inputs & checkboxes
  // -----------------------------------------------------------------------
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // -----------------------------------------------------------------------
  // Handle file selection
  // -----------------------------------------------------------------------
  const handleFileSelected = (file) => {
    setFormData((prev) => ({ ...prev, file }));
  };

  // -----------------------------------------------------------------------
  // Handle signature changes (base64 string)
  // -----------------------------------------------------------------------
  const handleSignatureChange = (signatureData) => {
    setFormData((prev) => ({ ...prev, signature_base64: signatureData }));
  };

  // -----------------------------------------------------------------------
  // T&C modal controls
  // -----------------------------------------------------------------------
  const openModal = (e) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  const acceptTerms = () => {
    setFormData((prev) => ({ ...prev, agree_terms: true }));
  };

  // -----------------------------------------------------------------------
  // Form submission
  // -----------------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    // 1. Validate required text fields
    const requiredFields = [
      "registration_id",
      "partner_first_name",
      "partner_last_name",
      "main_first_name",
      "main_last_name",
      "email",
      "main_user_code",
      "phone_number",
      "address_line_1",
      "address_line_2",
      "address_city",
      "postcode",
      "county",
    ];

    for (const field of requiredFields) {
      if (field === "registration_id") {
        if (formData.registration_id === null) {
          alert("Registration ID is missing.");
          return;
        }
      } else if (typeof formData[field] === "string" && !formData[field].trim()) {
        alert(`Please fill out the "${field}" field.`);
        return;
      }
    }

    // 2. Validate the required file & signature
    if (!formData.file) {
      alert("Please upload the required file/consent document.");
      return;
    }
    if (!formData.signature_base64) {
      alert("Please provide your electronic signature.");
      return;
    }

    // 3. Validate that all consent tick boxes have been checked
    if (
      !formData.consent_for_purchases ||
      !formData.confirm_correct_information ||
      !formData.data_sharing_privacy_consent ||
      !formData.rules_consent
    ) {
      alert("Please tick all consent boxes to proceed.");
      return;
    }

    // 4. Validate Terms & Conditions acceptance via modal
    if (!formData.agree_terms) {
      alert("You must accept the Terms & Conditions!");
      return;
    }

    console.log("Submitting Partner Account Consent form:", formData);

    try {
      // 5. Call the API with the updated payload (including registration_id)
      const result = await submitSubAccountConsent(formData);
      alert(`Consent submitted successfully! ID: ${result.id}`);
      // Optionally reset the form or navigate to a "Success" page
    } catch (error) {
      console.error("Submission error:", error);
      alert("Submission failed. Please try again or contact support.");
    }
  };

  // Compute the formatted partner full name (only for display in consent tick box 1)
  const formattedPartnerName = `${formatName(formData.partner_first_name)} ${formatName(formData.partner_last_name)}`;

  return (
    <div style={{ maxWidth: "700px", margin: "0 auto" }}>
      {/* Change the heading from Sub-Account Consent to Partner Consent */}
      <h1>Partner Consent</h1>
      <p>
        You are viewing <strong>/subaccount/consent</strong>. This page is pre-filled by URL query parameters.
      </p>

      <form onSubmit={handleSubmit}>
        {/* ---- Required Fields ---- */}
        <TextInput
          label="Partner First Name (required)"
          name="partner_first_name"
          value={formData.partner_first_name}
          onChange={handleChange}
        />
        <TextInput
          label="Partner Last Name (required)"
          name="partner_last_name"
          value={formData.partner_last_name}
          onChange={handleChange}
        />
        <TextInput
          label="Main First Name (required)"
          name="main_first_name"
          value={formData.main_first_name}
          onChange={handleChange}
        />
        <TextInput
          label="Main Last Name (required)"
          name="main_last_name"
          value={formData.main_last_name}
          onChange={handleChange}
        />
        <TextInput
          label="Email (required)"
          name="email"
          value={formData.email}
          onChange={handleChange}
          type="email"
        />
        <TextInput
          label="Main User Code (required)"
          name="main_user_code"
          value={formData.main_user_code}
          onChange={handleChange}
        />
        <TextInput
          label="Phone Number (required)"
          name="phone_number"
          value={formData.phone_number}
          onChange={handleChange}
          type="tel"
        />
        <TextInput
          label="Address Line 1 (required)"
          name="address_line_1"
          value={formData.address_line_1}
          onChange={handleChange}
        />
        <TextInput
          label="Address Line 2 (required)"
          name="address_line_2"
          value={formData.address_line_2}
          onChange={handleChange}
        />
        <TextInput
          label="City (required)"
          name="address_city"
          value={formData.address_city}
          onChange={handleChange}
        />
        <TextInput
          label="Postcode (required)"
          name="postcode"
          value={formData.postcode}
          onChange={handleChange}
        />
        <TextInput
          label="County (required)"
          name="county"
          value={formData.county}
          onChange={handleChange}
        />

        {/* ---- Consent Tick Boxes ---- */}
        <div style={{ margin: "1rem 0 0.5rem" }}>
          <strong>Please tick the boxes below to confirm your consent:</strong>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="consent_for_purchases"
              checked={formData.consent_for_purchases}
              onChange={handleChange}
            />
            &nbsp;
            <strong>Consent to Use the Platform:</strong> By signing this form, I, {formattedPartnerName}, confirm that I am giving my explicit consent to be added as a Partner Account holder under the main user {formData.main_first_name} {formData.main_last_name} on the TT International platform. I consent to use the platform for ticket purchasing, exchange, and resale under the management of the main account holder. I have read and understood the platform's Service Terms and Conditions and agree to comply with all rules. I understand that any purchases made under my Partner Account must comply with the event organizers' purchase limits and TT International's terms.
          </label>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="confirm_correct_information"
              checked={formData.confirm_correct_information}
              onChange={handleChange}
            />
            &nbsp;
            <strong>Confirmation of Accurate Personal Information:</strong> I confirm that the personal information provided for my Partner Account is accurate and truthful. I acknowledge that the use of false, fabricated, or misleading personal details may constitute fraud or misrepresentation, for which the main account holder and I may be held accountable.
          </label>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="data_sharing_privacy_consent"
              checked={formData.data_sharing_privacy_consent}
              onChange={handleChange}
            />
            &nbsp;
            <strong>Data Sharing and Privacy Consent:</strong> I consent to TT International processing and storing my personal information as required to manage ticket purchasing, resale, and rebate services. I understand that my personal information may be shared with the main user and necessary third parties to facilitate the resale process, and I consent to this. I am aware of my rights under GDPR and can request data modification or deletion by contacting management@ttinternationalltd.co.uk.
          </label>
        </div>
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="rules_consent"
              checked={formData.rules_consent}
              onChange={handleChange}
            />
            &nbsp;
            <strong>Liability and Responsibility:</strong> I acknowledge that the main user is responsible for managing and overseeing all ticket purchases made under my Partner Account, and I agree to comply with their instructions. I understand that any violation of the event organizer's rules or the platform's terms, including using bots or exceeding ticket purchase limits, may result in penalties or account suspension.
          </label>
        </div>

        {/* ---- File Uploader & Signature (required) ---- */}
        <FileUploader
          label="ID Upload: Please attach a valid photo ID for verification purposes. (File upload)"
          onFileSelected={handleFileSelected}
        />
        <SignaturePad
          label="Electronic Signature (required): By signing below, I confirm that I have read, understood, and agreed to all the above terms."
          onSignatureChange={handleSignatureChange}
        />

        {/* ---- Terms & Conditions Acceptance (via Modal) ---- */}
        <div style={{ marginBottom: "1rem" }}>
          <label>
            <input
              type="checkbox"
              name="agree_terms"
              checked={formData.agree_terms}
              onChange={() => {}}
              disabled
            />
            &nbsp;I have read and accept the Terms & Conditions (required)
          </label>
          &nbsp;
          <button onClick={openModal} style={{ marginLeft: "6px" }}>
            View T&C & Privacy
          </button>
        </div>

        <button type="submit">Submit Consent</button>
      </form>

      {isModalOpen && (
        <TermsModal onClose={closeModal} onAccept={acceptTerms} />
      )}
    </div>
  );
}

export default SubAccountConsent;
